import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SITE, whatsappLink } from "@/data/site";
import {
  czk,
  selectionTotal,
  toggleAddonSelection,
  toggleBundleSelection,
  bundleSavings,
  onPreselect,
} from "@/lib/price";
import { AddonPicker } from "./reservation/AddonPicker";
import { useAvailability, urgencyShort, refreshAvailability } from "@/hooks/useAvailability";
import { TERMS_TEXT, MARKETING_TEXT } from "@/lib/clubApi";
import { LocationToggle, VehicleSizeToggle } from "./reservation/QuickPickers";
import { ReservationSuccess } from "./reservation/ReservationSuccess";
import { useProgram } from "@/lib/programs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "bg-brand-bg border-brand-border focus-visible:ring-brand-accent text-white h-12";

const emptyForm = () => ({
  name: "",
  phone: "",
  email: "",
  car: "",
  location: "cestlice",
  note: "",
  addons: [],
  bundles: [],
  vehicleSize: "standard",
  joinClub: false,
  acceptTerms: false,
  marketingOptIn: false,
});

export default function Reservation() {
  const [form, setForm] = useState(emptyForm);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const availability = useAvailability();
  const { program, location: programLocation, setLocation: setProgramLocation } = useProgram();

  // the reservation branch and the promoted-program branch stay in sync (program decides the base price)
  useEffect(() => { setForm((f) => (f.location === programLocation ? f : { ...f, location: programLocation })); }, [programLocation]);
  const update = (k, v) => { if (k === "location") setProgramLocation(v); setForm((f) => ({ ...f, [k]: v })); };
  const toggleAddon = (key) =>
    setForm((f) => {
      const next = toggleAddonSelection(f, key);
      if (next.upgraded) {
        toast.success(`Máte všechny položky balíčku — přepnuto na ${next.upgraded.label}. Ušetříte ${czk(bundleSavings(next.upgraded))}.`);
      }
      return { ...f, addons: next.addons, bundles: next.bundles };
    });
  const toggleBundle = (key) => setForm((f) => ({ ...f, ...toggleBundleSelection(f, key) }));

  // Preselect from Pricing section ("Přidat" buttons)
  useEffect(
    () =>
      onPreselect((detail) => {
        setDone(null);
        setForm((f) => {
          if (detail.bundle) {
            return f.bundles.includes(detail.bundle) ? f : { ...f, ...toggleBundleSelection(f, detail.bundle) };
          }
          if (detail.addon) {
            const covered = SITE.bundles.filter((b) => f.bundles.includes(b.key)).flatMap((b) => b.addons);
            if (f.addons.includes(detail.addon) || covered.includes(detail.addon)) return f;
            const next = toggleAddonSelection(f, detail.addon);
            return { ...f, addons: next.addons, bundles: next.bundles };
          }
          return f;
        });
      }),
    []
  );

  const { total, addonsPrice, bundlePrice, surcharge, surchargePct } = useMemo(() => selectionTotal({ ...form, basePrice: program.price }), [form, program.price]);
  const addonsTotal = addonsPrice + bundlePrice;

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Napište prosím své jméno.");
    if (form.phone.replace(/\D/g, "").length < 9) return toast.error("Zadejte prosím platné telefonní číslo.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return toast.error("Zadejte prosím platný e-mail pro potvrzení.");
    if (form.joinClub && !form.acceptTerms) return toast.error("Pro vstup do klubu je potřeba přijmout pravidla členství.");
    setLoading(true);
    try {
      const { vehicleSize, joinClub, acceptTerms, marketingOptIn, ...rest } = form;
      const payload = { ...rest, vehicle_size: vehicleSize, email: form.email.trim(), join_club: joinClub,
        accept_terms: joinClub && acceptTerms, marketing_opt_in: joinClub && marketingOptIn };
      const { data } = await axios.post(`${API}/reservations`, payload);
      setDone(data);
      refreshAvailability().catch(() => {});
      toast.success("Poptávku jsme přijali. Ozveme se vám a potvrdíme termín.");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Něco se pokazilo. Zkuste to znovu nebo napište na WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <ReservationSuccess
        reservation={done}
        onReset={() => {
          setDone(null);
          setForm(emptyForm());
          setShowDetails(false);
        }}
      />
    );
  }

  return (
    <section
      id="reservation"
      data-testid="reservation-section"
      className="relative bg-brand-bg py-24 lg:py-32 border-t border-brand-border"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-xs uppercase tracking-[0.3em] text-brand-accent">Rezervace</div>
              <div
                data-testid="reservation-urgency"
                className="inline-flex items-center gap-2 border border-brand-accent/50 text-brand-accent px-2.5 py-1 text-[10px] uppercase tracking-[0.15em]"
              >
                <span className="w-1.5 h-1.5 bg-brand-accent rounded-full animate-pulse" />
                {urgencyShort(availability)}
              </div>
            </div>
            <h2 className="mt-4 font-display font-black text-4xl lg:text-6xl leading-[1.05] tracking-tight">
              Jméno, telefon, e-mail,{" "}
              <span className="italic font-light">hotovo.</span>
            </h2>
            <p className="mt-6 text-brand-muted leading-relaxed max-w-md">
              Tři pole a jedno kliknutí. Odešlete poptávku, my se vám ozveme a potvrdíme termín,
              který vám sedne. Shrnutí dostanete e-mailem. Žádné skryté poplatky.
            </p>

            <ul className="mt-10 space-y-3 text-sm">
              {[
                "Poptávka za 4 sekundy — bez registrace",
                "Termín domluvíme telefonicky",
                "Bez zálohy, platíte až při předání",
                "Zrušení či přesun zdarma do dne předem",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-white/90">
                  <span className="w-1.5 h-1.5 bg-brand-accent shrink-0" />
                  {t}
                </li>
              ))}
            </ul>

            <div className="mt-10 text-sm">
              <div className="text-brand-muted text-xs uppercase tracking-[0.2em]">Radši WhatsApp?</div>
              <a
                href={whatsappLink()}
                target="_blank"
                rel="noreferrer"
                data-testid="reservation-whatsapp-link"
                className="mt-1 inline-block text-white hover:text-brand-accent text-lg font-semibold"
              >
                {SITE.whatsappNumber}
              </a>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="reservation-form"
            className="lg:col-span-7 bg-brand-surface border border-brand-border p-6 lg:p-10 space-y-6"
          >
            <div className="grid sm:grid-cols-3 gap-3">
              <Input
                data-testid="reservation-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Vaše jméno *"
                autoComplete="name"
                className={inputCls}
              />
              <Input
                data-testid="reservation-phone"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="Telefon * (+420 ...)"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                className={inputCls}
              />
              <Input
                data-testid="reservation-email"
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="E-mail * (potvrzení)"
                autoComplete="email"
                className={inputCls}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <LocationToggle value={form.location} onChange={(v) => update("location", v)} />
              <VehicleSizeToggle value={form.vehicleSize} onChange={(v) => update("vehicleSize", v)} />
            </div>

            <AddonPicker
              selected={form.addons}
              bundles={form.bundles}
              onToggle={toggleAddon}
              onToggleBundle={toggleBundle}
            />

            <button
              type="button"
              onClick={() => setShowDetails((s) => !s)}
              data-testid="reservation-toggle-details"
              className="text-xs uppercase tracking-[0.2em] text-brand-muted hover:text-white transition-colors"
            >
              {showDetails ? "− Skrýt detaily" : "+ Doplnit vozidlo a poznámku (volitelné)"}
            </button>

            <AnimatePresence initial={false}>
              {showDetails && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden space-y-3"
                  data-testid="reservation-details"
                >
                  <Input
                    data-testid="reservation-car"
                    value={form.car}
                    onChange={(e) => update("car", e.target.value)}
                    placeholder="Vozidlo (např. Škoda Superb)"
                    className={inputCls}
                  />
                  <Textarea
                    data-testid="reservation-note"
                    value={form.note}
                    onChange={(e) => update("note", e.target.value)}
                    placeholder="Poznámka..."
                    rows={2}
                    className="bg-brand-bg border-brand-border focus-visible:ring-brand-accent text-white resize-none"
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="border border-brand-border p-4 space-y-2" data-testid="reservation-club-optin">
              <label className="flex items-start gap-3 text-sm text-white/90 cursor-pointer">
                <input type="checkbox" data-testid="reservation-join-club" checked={form.joinClub} onChange={(e) => update("joinClub", e.target.checked)} className="mt-0.5 accent-[#00DF82]" />
                <span><b>Připojit se zdarma do CARSPA CLUB</b> — kontakt už máme, nic dalšího nevyplňujete. Zatím zdarma pro prvních 500 členů, bez předplatného.</span>
              </label>
              {form.joinClub && (
                <div className="pl-7 space-y-2">
                  <label className="flex items-start gap-3 text-xs text-white/80 cursor-pointer">
                    <input type="checkbox" data-testid="reservation-club-terms" checked={form.acceptTerms} onChange={(e) => update("acceptTerms", e.target.checked)} className="mt-0.5 accent-[#00DF82]" />
                    <span>{TERMS_TEXT} *</span>
                  </label>
                  <label className="flex items-start gap-3 text-xs text-brand-muted cursor-pointer">
                    <input type="checkbox" data-testid="reservation-club-marketing" checked={form.marketingOptIn} onChange={(e) => update("marketingOptIn", e.target.checked)} className="mt-0.5 accent-[#00DF82]" />
                    <span>{MARKETING_TEXT}</span>
                  </label>
                </div>
              )}
            </div>

            <div className="border-t border-brand-border pt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div data-testid="reservation-total">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-muted">Celkem</div>
                <div className="font-display font-black text-3xl text-white leading-none mt-1">
                  {czk(total)}
                </div>
                <div className="text-xs text-brand-muted mt-1">
                  <span data-testid="reservation-program">{program.title} {czk(program.price)}</span>
                  {addonsTotal > 0 && (
                    <span className="text-brand-accent"> + doplňky {czk(addonsTotal)}</span>
                  )}
                  {surcharge > 0 && (
                    <span className="text-white/80" data-testid="reservation-surcharge"> + SUV {surchargePct} % ({czk(surcharge)})</span>
                  )}
                  {" · "}platba při předání
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="reservation-submit-button"
                className="inline-flex items-center justify-center gap-3 bg-brand-accent text-black font-semibold px-8 h-14 hover:bg-brand-accentHover transition-all disabled:opacity-60 disabled:cursor-not-allowed text-base"
              >
                {loading ? "Odesílám..." : `Odeslat poptávku · ${czk(total)}`}
                {!loading && <span>→</span>}
              </button>
            </div>
            <div className="text-[11px] text-brand-muted">
              Odesláním souhlasíte se zpracováním údajů pro účely rezervace.
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
